import { useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { Handshake, Percent, RotateCw, ShieldAlert, TriangleAlert, Trophy, Users } from 'lucide-react';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  MoneyText,
  SegmentedTabs,
  Select,
  Skeleton,
  TrendBadge,
} from '@/ui';
import { hasPermission, isOrgAdmin } from '@/auth/types';
import { useSessionStore } from '@/auth/session.store';
import {
  REPORT_RANGES,
  findReportRange,
  rangeFromParam,
  rangeStart,
  type ReportRangeKey,
} from '@/lib/report-range';
import { previousWindow, trendDelta } from '@/lib/trend';
import { winRatePercent } from '@/lib/report-volume';
import { formatDateTime } from '@/features/admin/format';
// Sayfalama kalibi admin liste ekranlariyla ORTAK: ayni `{items, page}` sozlesmesini iki ayri
// hook ile tuketmek, "hasMore mi returned mi" sorusunu ikiye bolerdi.
import { flattenPages, usePagedQuery } from '@/features/admin/use-paged-query';
import { LoadMore } from '@/features/admin/ListStatus';
import { useUrlRange } from './useUrlRange';
import { BarSeries, type BarDatum } from './components/BarSeries';
import {
  fetchDecisionTime,
  fetchMemberActivity,
  fetchOffersSummary,
  fetchTasksSummary,
  fetchWarnedTasks,
  WARNED_SORT_DEFAULT,
  type DecisionTime,
  type MemberActivityRow,
  type OffersSummary,
  type TasksSummary,
  type WarnedSort,
  type WarnedTaskRow,
} from './api';
import {
  REPORT_MEMBER_TYPE_LABEL_KEYS,
  TASK_STATUS_LABEL_KEYS,
  translateCode,
} from './labels';
import { reportKeys } from './query-keys';
import styles from './Reports.module.css';

/**
 * ======================= ISLETME RAPORLARI (/buro/raporlar) =================
 *
 * Mobil `Reports` ekraninin web karsiligi: 30g / 90g / tum zamanlar araligi,
 * dort sayi kutusu, para birimi ve durum kirilimlari, ve YALNIZ YONETICIYE
 * personel performansi.
 *
 * ------------------ TUTARLAR "HACIM" DEGIL, KUR TOPLAMI --------------------
 * Uc rapor SQL'i de `COALESCE(sum(o.offer_price), 0)` yaziyor — MIKTARLA
 * CARPIM YOK. Yani bu ekrandaki para rakamlari BIRIM KURLARIN TOPLAMI, ciro
 * degil (2711 teklifin kur toplami 119.767 TL cikiyor; ortalama kur ~44 TL).
 *
 * Mobil ekran bu sayiyi "Kazanılan hacim" diye basiyor; burada BILINCLI olarak
 * oyle etiketlenmedi ve markali tip (UnitRate) <MoneyText total=…> icine
 * gecirilmesini DERLEME HATASI yapiyor. Gercek ciro icin /buro/panel — orada
 * carpimi sunucu yapiyor. Gerekce: src/lib/report-volume.ts.
 *
 * ------------------------------- KAPILAR -----------------------------------
 * offers-summary / tasks-summary : `viewReport` yetkisi (sunucu 'PERMISSION_DENIED').
 * member-activity                : ORG YONETICISI — bir yetki kodu DEGIL,
 *                                  verilemez de (sunucu 'ADMIN_REQUIRED').
 * Ikisi de canli sunucuda `buropersonel@demo.com` ile sinandi.
 *
 * Yonetici ORTUK OLARAK TAM YETKILIDIR: `hasPermission` yoneticiyi grant
 * listesine karsi SINAMAZ (sunucu yonetici icin grant satiri yazmaz, liste bos
 * gelebilir). Bu yuzden burada da ayrica `|| isAdmin` yazilmiyor — kontrol
 * zaten src/auth/types.ts icinde tek yerde.
 * ========================================================================== */

export function ReportsPage() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const snapshot = useSessionStore((state) => state.snapshot);
  const canViewReport = hasPermission(snapshot, 'viewReport');
  const admin = isOrgAdmin(snapshot);

  const [rangeKey, setRangeKey] = useUrlRange();
  const range = findReportRange(rangeKey);
  const from = rangeFromParam(range);

  /**
   * ONCEKI DONEM PENCERESI — "gecen doneme gore" oklari icin (mobil paritesi).
   *
   * 'Tum zamanlar'da KIYAS YOK ve olamaz: acik uclu bir aralikta "onceki donem"
   * diye bir sey tanimli degil. `undefined` donuyor, sorgu hic calismiyor ve
   * rozetler cizilmiyor — uydurma bir kiyas gostermektense hic gostermemek dogru.
   */
  const prevWindow = useMemo(() => {
    const start = rangeStart(range);
    if (!start) return undefined;
    return previousWindow(start, new Date());
  }, [range]);

  /**
   * Uc uc TEK `useQueries` ile. Ayri ayri `useQuery` yazmak da calisirdi ama
   * yukleniyor/hata durumunu uc ayri yerde birlestirmek gerekirdi; burada tek
   * dizi uzerinden okunuyor.
   *
   * `member-activity` YONETICI DEGILSE HIC ISTENMEZ (`enabled: admin`):
   * sunucu 403 dondurecegi icin istek bosa gider ve konsolu hata ile doldurur.
   */
  const [
    offersQuery,
    prevOffersQuery,
    tasksQuery,
    membersQuery,
    decisionQuery,
  ] = useQueries({
    queries: [
      {
        queryKey: reportKeys.offersSummary(rangeKey),
        queryFn: () => fetchOffersSummary(from),
        enabled: canViewReport,
      },
      {
        queryKey: reportKeys.offersSummaryPrevious(rangeKey),
        queryFn: () =>
          fetchOffersSummary(prevWindow?.start.toISOString(), prevWindow?.end.toISOString()),
        // 'all' araliginda pencere yok -> istek hic gitmiyor.
        enabled: canViewReport && prevWindow !== undefined,
      },
      {
        queryKey: reportKeys.tasksSummary(rangeKey),
        queryFn: () => fetchTasksSummary(from),
        enabled: canViewReport,
      },
      {
        queryKey: reportKeys.memberActivity(rangeKey),
        queryFn: () => fetchMemberActivity(from),
        enabled: canViewReport && admin,
      },
      // `viewReport` ile acik, yonetici sarti YOK: karar suresi buronun KENDI islemlerine ait
      // (sunucu da yalniz viewReport istiyor). Uyari listesi bu diziden CIKARILDI: sayfalanan
      // bir liste `useInfiniteQuery` ister ve `useQueries` onu tasiyamaz — kendi bileseninde.
      {
        queryKey: reportKeys.decisionTime(rangeKey),
        queryFn: () => fetchDecisionTime(from),
        enabled: canViewReport,
      },
    ],
  });

  // Ozet bayat kalabilir ve rotaya derin baglantiyla gelinebiliyor — ekranin
  // kendi kapisi bu yuzden var (rota zaten `viewReport` ile korunuyor).
  if (!canViewReport) {
    return (
      <Card padding="lg">
        <EmptyState
          tone="error"
          icon={<ShieldAlert size={28} aria-hidden="true" />}
          title={t('report.noPermission')}
          description={t('report.noPermissionDetail')}
        />
      </Card>
    );
  }

  const loading = offersQuery.isPending || tasksQuery.isPending;
  const failed = offersQuery.isError || tasksQuery.isError;

  return (
    <div className={styles.page}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.title}>{t('report.title')}</h1>
          <p className={styles.subtitle}>{t('report.scopeNote')}</p>
        </div>
        <Button
          tone="neutral"
          size="sm"
          iconLeft={<RotateCw size={16} aria-hidden="true" />}
          loading={offersQuery.isFetching || tasksQuery.isFetching}
          onClick={() => {
            void client.invalidateQueries({ queryKey: reportKeys.all });
          }}
        >
          {t('report.refresh')}
        </Button>
      </div>

      <SegmentedTabs
        label={t('report.rangeLabel')}
        value={rangeKey}
        onChange={setRangeKey}
        items={REPORT_RANGES.map((item) => ({
          value: item.key,
          // `labelKey` bir ANAHTAR; t() cagri yerinde yapilir (modul seviyesinde
          // yapilsaydi dil degisiminde bayat kalirdi).
          label: t(item.labelKey),
        }))}
      />

      {failed ? (
        <Card padding="lg">
          <EmptyState
            tone="error"
            icon={<TriangleAlert size={28} aria-hidden="true" />}
            title={t('report.loadFailed')}
            description={t('report.loadFailedDetail')}
            action={
              <Button
                tone="neutral"
                size="sm"
                onClick={() => {
                  void client.invalidateQueries({ queryKey: reportKeys.all });
                }}
              >
                {t('common.retry')}
              </Button>
            }
          />
        </Card>
      ) : loading ? (
        <div className={styles.loading}>
          <Skeleton shape="block" height="96px" />
          <Skeleton shape="block" height="160px" />
          <Skeleton shape="block" height="160px" />
        </div>
      ) : (
        <>
          <SummaryTiles offers={offersQuery.data} previous={prevOffersQuery?.data} />
          <CurrencyBreakdown offers={offersQuery.data} />
          <StatusBreakdown tasks={tasksQuery.data} />
          {/* Personel performansi YALNIZ YONETICIYE. Bolum, yetkisi olmayan
              kullanicida hic RENDER EDILMEZ — gizlenmis ama DOM'da duran bir
              bolum, calisanlarin birbirinin verimini gormesi demek olurdu
              (KVKK'da calisan izleme). Sunucu da bu ucu admine kilitliyor. */}
          <DecisionTimeSection query={decisionQuery} />
          <WarnedTasksSection rangeKey={rangeKey} from={from} />
          {admin ? <MemberActivitySection query={membersQuery} /> : null}
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sayi kutulari                                                               */
/* -------------------------------------------------------------------------- */

function SummaryTiles({
  offers,
  previous,
}: {
  offers: OffersSummary | undefined;
  previous: OffersSummary | undefined;
}) {
  const { t } = useTranslation();
  if (!offers) return null;

  const winRate = winRatePercent(offers.wonCount, offers.offerCount);
  const prevWinRate = previous ? winRatePercent(previous.wonCount, previous.offerCount) : null;

  return (
    <div className={styles.tiles}>
      <article className={styles.tile}>
        <span className={styles.tileLabel}>
          <Handshake size={14} aria-hidden="true" /> {t('report.offersGiven')}
        </span>
        <span className={styles.tileValueRow}>
          <span className={styles.tileValue}>{offers.offerCount}</span>
          <TrendBadge delta={trendDelta(offers.offerCount, previous?.offerCount ?? 0)} />
        </span>
      </article>

      <article className={styles.tile}>
        <span className={styles.tileLabel}>
          <Trophy size={14} aria-hidden="true" /> {t('report.offersWon')}
        </span>
        <span className={styles.tileValueRow}>
          <span className={styles.tileValue}>{offers.wonCount}</span>
          <TrendBadge delta={trendDelta(offers.wonCount, previous?.wonCount ?? 0)} />
        </span>
      </article>

      <article className={styles.tile}>
        <span className={styles.tileLabel}>
          <Percent size={14} aria-hidden="true" /> {t('report.winRate')}
        </span>
        <span className={styles.tileValueRow}>
          <span className={styles.tileValue}>{winRate === null ? '—' : `%${winRate}`}</span>
          {/* Kazanma orani YUZDE PUANI degil, oranin kendisindeki degisim
              olarak kiyaslanir — diger kutularla ayni okuma. */}
          <TrendBadge delta={trendDelta(winRate ?? 0, prevWinRate ?? 0)} />
        </span>
        {prevWinRate !== null ? (
          <span className={styles.tileHint}>
            {t('trend.prevPeriod')}: %{prevWinRate}
          </span>
        ) : null}
      </article>

      {/* KUR TOPLAMI — hacim DEGIL. `rate` propu bunu tip duzeyinde zorluyor:
          `total` yazmak derleme hatasi olurdu (bkz. src/ui/MoneyText.tsx). */}
      <article className={styles.tile}>
        <span className={styles.tileLabel}>{t('report.wonRateSum')}</span>
        <span className={styles.tileValueRow}>
          <MoneyText rate={offers.wonOfferRate} currency="TRY" size="h2" />
          <TrendBadge delta={trendDelta(offers.wonOfferRate, previous?.wonOfferRate ?? 0)} />
        </span>
        <span className={styles.tileHint}>{t('report.rateSumHint')}</span>
      </article>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Kirilimlar                                                                  */
/* -------------------------------------------------------------------------- */

function CurrencyBreakdown({ offers }: { offers: OffersSummary | undefined }) {
  const { t } = useTranslation();
  if (!offers) return null;

  if (offers.rows.length === 0) {
    return (
      <Card padding="lg">
        <EmptyState title={t('report.noOffers')} description={t('report.noOffersDetail')} />
      </Card>
    );
  }

  const data: BarDatum[] = offers.rows.map((row) => ({
    id: row.currency,
    label: row.currency,
    value: row.wonOfferRate,
    display: <MoneyText rate={row.wonOfferRate} currency="TRY" />,
    hint: t('report.wonOfCount', { won: row.wonCount, total: row.offerCount }),
  }));

  return (
    <Card padding="md">
      <BarSeries
        caption={t('report.byCurrency')}
        labelHeading={t('report.currencyColumn')}
        valueLabel={t('report.wonRateSum')}
        data={data}
      />
    </Card>
  );
}

function StatusBreakdown({ tasks }: { tasks: TasksSummary | undefined }) {
  const { t } = useTranslation();
  if (!tasks) return null;

  if (tasks.rows.length === 0) {
    return (
      <Card padding="lg">
        <EmptyState title={t('report.noTasks')} description={t('report.noTasksDetail')} />
      </Card>
    );
  }

  const data: BarDatum[] = tasks.rows.map((row) => ({
    id: row.statusCode,
    label: translateCode(t, TASK_STATUS_LABEL_KEYS, row.statusCode),
    // Barlar ISLEM SAYISINI gosteriyor, kur toplamini degil: sayilar
    // karsilastirilabilir bir buyukluk, kur toplamlari degil.
    value: row.taskCount,
    display: String(row.taskCount),
    hint: t('report.statusRateSum', { value: row.totalRate.toLocaleString('tr-TR') }),
  }));

  return (
    <Card padding="md">
      <BarSeries
        caption={t('report.taskStatuses')}
        labelHeading={t('report.statusColumn')}
        valueLabel={t('report.taskCountColumn')}
        data={data}
      />
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Personel performansi — YALNIZ YONETICI                                      */
/* -------------------------------------------------------------------------- */

type MembersQuery = {
  data: readonly MemberActivityRow[] | undefined;
  isPending: boolean;
  isError: boolean;
};

function MemberActivitySection({ query }: { query: MembersQuery }) {
  const { t } = useTranslation();

  if (query.isPending) return <Skeleton shape="block" height="160px" />;

  if (query.isError) {
    return (
      <Card padding="md">
        <EmptyState
          tone="error"
          icon={<TriangleAlert size={24} aria-hidden="true" />}
          title={t('report.memberLoadFailed')}
        />
      </Card>
    );
  }

  const rows = query.data ?? [];

  return (
    <Card padding="md">
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>
          <Users size={16} aria-hidden="true" /> {t('report.memberPerformance')}
        </h2>
        <p className={styles.sectionNote}>{t('report.memberAdminOnly')}</p>
      </div>

      {rows.length === 0 ? (
        <EmptyState title={t('report.noMemberActivity')} />
      ) : (
        <ul className={styles.memberList}>
          {rows.map((row) => (
            <li key={row.appUserId} className={styles.memberRow}>
              <div className={styles.memberHead}>
                <span className={styles.memberName}>
                  {row.userFullName === '' ? t('report.unnamedMember') : row.userFullName}
                </span>
                <span className={styles.memberChips}>
                  <Chip size="sm" tone={row.memberTypeCode === 'admin' ? 'primary' : 'neutral'}>
                    {translateCode(t, REPORT_MEMBER_TYPE_LABEL_KEYS, row.memberTypeCode)}
                  </Chip>
                  {/* Uyeligi pasiflesmis personel LISTEDE KALIR: gecmis donem
                      raporu, o kisi cikarildi diye eksilmemeli. */}
                  {row.isActive ? null : (
                    <Chip size="sm" tone="warning">
                      {t('report.memberInactive')}
                    </Chip>
                  )}
                </span>
              </div>
              <p className={styles.memberStats}>
                {t('report.wonOfCount', { won: row.wonCount, total: row.offerCount })}
                {' · '}
                {t('report.wonRateSum')}: <MoneyText rate={row.wonOfferRate} currency="TRY" />
              </p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// `data?: T` DEGIL `data: T | undefined`: tsconfig'de `exactOptionalPropertyTypes` acik,
// ikisi ayni sey degil ve TanStack sonucu opsiyonel ALAN degil, undefined DEGER doner
// (MembersQuery ile ayni sekil).
type DecisionQuery = {
  data: DecisionTime | undefined;
  isPending: boolean;
  isError: boolean;
};

/**
 * ORTALAMA KARAR SURESI — talep acilisindan teklifin kabulune.
 *
 * Ortalama TEK BASINA okunamaz: iki islemin ortalamasi ile iki yuz islemin ortalamasi ayni
 * sayiyi gosterse bile ayni sey degildir. Bu yuzden islem sayisi HER ZAMAN yaninda.
 *
 * `decided_at` alani 2026-08-30 migration'iyla geldi; ondan ONCE biten islemler ortalamaya
 * girmez. Eksik olcumu belirtmemek, dusuk bir sayiyi "hizlandik" diye okutur.
 */
function DecisionTimeSection({ query }: { query: DecisionQuery }) {
  const { t } = useTranslation();

  if (query.isPending) return <Skeleton shape="block" height="120px" />;
  if (query.isError) {
    return (
      <Card padding="md">
        <EmptyState
          tone="error"
          icon={<TriangleAlert size={24} aria-hidden="true" />}
          title={t('report.decisionLoadFailed')}
        />
      </Card>
    );
  }

  const data = query.data;
  return (
    <Card padding="md">
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>{t('report.decisionTime')}</h2>
        <p className={styles.sectionNote}>{t('report.decisionTimeNote')}</p>
      </div>
      {!data || data.decidedCount === 0 ? (
        <EmptyState title={t('report.noDecisionData')} />
      ) : (
        <div className={styles.tiles}>
          <div className={styles.tile}>
            <p className={styles.tileLabel}>{t('report.decisionAverage')}</p>
            <p className={styles.tileValue}>{formatDuration(t, data.avgSeconds)}</p>
          </div>
          <div className={styles.tile}>
            <p className={styles.tileLabel}>{t('report.decisionCount')}</p>
            <p className={styles.tileValue}>{data.decidedCount}</p>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * SANIYEYI okunur sureye cevirir. `Intl.RelativeTimeFormat` KULLANILMIYOR: o "3 saat once"
 * gibi GECMISE isaret eden bir metin uretir, buradaki deger ise bir SURE.
 */
const formatDuration = (t: TFunction, seconds: number): string => {
  if (seconds < 60) return t('report.durationSeconds', { value: seconds });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t('report.durationMinutes', { value: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('report.durationHours', { value: hours });
  return t('report.durationDays', { value: Math.round(hours / 24) });
};

/** Siralama secenekleri — `value` sunucunun beyaz listesindeki anahtar, birebir gonderilir. */
const WARNED_SORT_OPTIONS: ReadonlyArray<{ value: WarnedSort; labelKey: string }> = [
  { value: 'warnedAt:desc', labelKey: 'filter.sortNewest' },
  { value: 'warnedAt:asc', labelKey: 'filter.sortOldest' },
  { value: 'volume:desc', labelKey: 'filter.sortAmountDesc' },
  { value: 'volume:asc', labelKey: 'filter.sortAmountAsc' },
  { value: 'customerName:asc', labelKey: 'filter.sortCustomerAsc' },
];

/**
 * ODEME UYARISI ALAN ISLEMLER — SAYFALANIR.
 *
 * `viewReport` yetiyor, yonetici sarti YOK: uyari buronun KAZANDIGI islemde olusur ve musteri
 * kimligi kazanan buroya zaten gorunur. Sunucu da ayni kurali uyguluyor.
 *
 * ------------------------------ NEDEN SAYFALAMA ----------------------------
 * Uc onceden sabit `LIMIT 50` uyguluyordu. Elli birinci uyari ekrandan SESSIZCE dusuyordu:
 * hicbir yerde "kirpildi" yazmadigi icin liste TAM gorunuyordu ve "bu donemde 56 uyari var"
 * diyen ozet kutusu ile alttaki liste birbirini tutmuyordu. Artik zarf /v1/tasks ve denetim
 * kaydiyla ayni: `{ items, page }`, `total` YOK, yalniz `hasMore`.
 *
 * SIRALAMA YEREL STATE'te, adres cubugunda DEGIL: sayfadaki tek liste bu ve aralik zaten
 * URL'de. Siralamayi da URL'e tasimak, rapor baglantilarini paylasirken anlami olmayan bir
 * ikinci parametre uretirdi. Siralama degisince `usePagedQuery` YENI bir anahtar gorur ve
 * listeyi bastan kurar — ofseti ayrica sifirlayan bir kod yolu YOK (olsaydi unutulurdu).
 *
 * Tutar GERCEK HACIM (`taskAmount * offerPrice`); markali tip bunu garanti ediyor.
 */
function WarnedTasksSection({
  rangeKey,
  from,
}: {
  rangeKey: ReportRangeKey;
  from: string | undefined;
}) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<WarnedSort>(WARNED_SORT_DEFAULT);

  const query = usePagedQuery<WarnedTaskRow>(reportKeys.warnedTasks(rangeKey, sort), (param) =>
    fetchWarnedTasks({ from, offset: param.offset, snapshotAt: param.snapshotAt, sort }),
  );

  const rows = flattenPages(query.data);

  const head = (
    <div className={styles.sectionHead}>
      <h2 className={styles.sectionTitle}>
        <TriangleAlert size={16} aria-hidden="true" /> {t('report.warnedTasks')}
      </h2>
      <p className={styles.sectionNote}>{t('report.warnedTasksNote')}</p>
    </div>
  );

  if (query.isPending) return <Skeleton shape="block" height="160px" />;
  if (query.isError) {
    return (
      <Card padding="md">
        <EmptyState
          tone="error"
          icon={<TriangleAlert size={24} aria-hidden="true" />}
          title={t('report.warnedLoadFailed')}
          action={
            <Button
              tone="neutral"
              size="sm"
              onClick={() => {
                void query.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Card padding="md">
      {head}

      {/* Siralama, liste BOSKEN de cizilir: secenegi ancak satir varken gostermek, bos
          listeyi "siralama secilemez" gibi okuturdu. */}
      <div className={styles.sortRow}>
        <label className={styles.sortLabel} htmlFor="warned-sort">
          {t('filter.sort')}
        </label>
        <Select
          id="warned-sort"
          size="sm"
          value={sort}
          onChange={(event) => {
            setSort(event.target.value as WarnedSort);
          }}
          options={WARNED_SORT_OPTIONS.map((option) => ({
            value: option.value,
            label: t(option.labelKey),
          }))}
        />
      </div>

      {rows.length === 0 ? (
        // Bos liste burada IYI HABER: uyari alan islem yok demek.
        <EmptyState title={t('report.noWarnedTasks')} />
      ) : (
        <>
          <ul className={styles.memberList}>
            {rows.map((row) => (
              <li key={row.taskId} className={styles.memberRow}>
                <div className={styles.memberHead}>
                  <span className={styles.memberName}>{row.customerName}</span>
                  <MoneyText total={row.volume} currency={row.counterCurrency} size="caption" />
                </div>
                <span className={styles.tileLabel}>
                  {row.taskTypeCode} · {formatDateTime(row.warnedAt)}
                </span>
              </li>
            ))}
          </ul>
          <LoadMore
            hasNextPage={query.hasNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            onLoadMore={() => {
              void query.fetchNextPage();
            }}
          />
        </>
      )}
    </Card>
  );
}
